import { useEffect, useState } from "react";
import { Box, Typography, Grid, Select, MenuItem, Button } from "@mui/material";
import { getWellConfig } from "../../utils/wellsConfig";
import { PRC_WELL } from "../../utils/PRC_WELL";
import { ProgressDialog } from "./progressPopup";
import axios from "../../utils/axiosInstance";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function StartStopTab({
  startStopSettings,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) {
  const [emergencyValues, setEmergencyValues] = useState(startStopSettings);
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");

  useEffect(() => {
    setEmergencyValues(startStopSettings);
  }, [startStopSettings]);

  const handleChange = (label, newValue) => {
    setEmergencyValues((prev) =>
      prev.map((item) =>
        item.label === label ? { ...item, value: newValue } : item
      )
    );
  };

  const handleSubmit = async (label) => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate(label);
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async (label) => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let offset = "";
      let registerList = [wellConfig.COMP_SD, wellConfig.MAND_SHUTIN_SD];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      if (label === "Compressor SD on Well SD") {
        offset = wellConfig.COMP_SD - 40001;
      } else if (label === "Mandatory Shutin SD") {
        offset = wellConfig.MAND_SHUTIN_SD - 40001;
      }
      const getValue = PRC_WELL.getValueFromWell(emergencyValues, label);
      cmd += "," + offset + "," + getValue.value;
      console.log("Sending command:", cmd);
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFaultClear = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      faultClear();
    }, 100); // 100ms is enough to show the dialog
  };

  const handleSubmitStartPlgLift = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      startPlgLift();
    }, 100); // 100ms is enough to show the dialog
  };

  const handleSubmitStopPlgLift = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      stopPlgLift();
    }, 100); // 100ms is enough to show the dialog
  };

  const handleSubmitDisableWell = async () => {
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 1: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 2: Give React a chance to render before doing heavy work
    setTimeout(() => {
      disableWell();
    }, 100); // 100ms is enough to show the dialog
  };

  const faultClear = async () => {
    let pollInterval = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForTimeChange = ({
      unitId,
      lastTime,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.get(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
          );

          const currentTime = Number(resp.data.data?.time || 0);

          console.log("⏳ Polling...", { lastTime, currentTime });

          if (currentTime && currentTime !== lastTime) {
            stopPolling();
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
            onSuccess(currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let faultClear = "";
      // Step 1: get initial time
      const initialResp = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      console.log("Initial time:", lastTime);
      const id = wellConfig.ID;
      if (id === 1) {
        faultClear = 4096;
      } else if (id === 2) {
        faultClear = 8192;
      } else if (id === 3) {
        faultClear = 16384;
      } else {
        faultClear = 32768;
      }
      cmd += "," + (40094 - 40001) + "," + faultClear;
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        { unitId, cmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");

      // === Step 3: poll DURING 30s ===
      pollForTimeChange({
        unitId,
        lastTime,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Fault cleared after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === Step 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            {
              headers: {
                id: "anything",
                "Content-Type": "application/json",
              },
            }
          );

          console.log("🟢 P command sent, polling again...");

          // === Step 5: poll AFTER P ===
          pollForTimeChange({
            unitId,
            lastTime,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Fault cleared after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const startPlgLift = async () => {
    let pollInterval = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForTimeChange = ({
      unitId,
      lastTime,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.get(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
          );

          const currentTime = Number(resp.data.data?.time || 0);

          console.log("⏳ Polling...", { lastTime, currentTime });

          if (currentTime && currentTime !== lastTime) {
            stopPolling();
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
            onSuccess(currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let startPlg = "";
      // Step 1: get initial time
      const initialResp = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      console.log("Initial time:", lastTime);
      const id = wellConfig.ID;
      if (id === 1) {
        startPlg = 64;
      } else if (id === 2) {
        startPlg = 128;
      } else if (id === 3) {
        startPlg = 256;
      } else {
        startPlg = 512;
      }
      cmd += "," + (40093 - 40001) + "," + startPlg;
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // === Step 3: poll DURING 30s ===
      pollForTimeChange({
        unitId,
        lastTime,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Fault cleared after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === Step 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            { unitId, cmd: pCmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,},
            {
              headers: {
                id: "anything",
                "Content-Type": "application/json",
              },
            }
          );

          console.log("🟢 P command sent, polling again...");

          // === Step 5: poll AFTER P ===
          pollForTimeChange({
            unitId,
            lastTime,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Fault cleared after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const stopPlgLift = async () => {
    let pollInterval = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForTimeChange = ({
      unitId,
      lastTime,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.get(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
          );

          const currentTime = Number(resp.data.data?.time || 0);

          console.log("⏳ Polling...", { lastTime, currentTime });

          if (currentTime && currentTime !== lastTime) {
            stopPolling();
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
            onSuccess(currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let stopPlg = "";
      // Step 1: get initial time
      const initialResp = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/plungers/${unitId}`
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      console.log("Initial time:", lastTime);
      const id = wellConfig.ID;
      if (id === 1) {
        stopPlg = 1;
      } else if (id === 2) {
        stopPlg = 2;
      } else if (id === 3) {
        stopPlg = 4;
      } else {
        stopPlg = 8;
      }
      cmd += "," + (40093 - 40001) + "," + stopPlg;
      // === Step 2: Send plunger command ===
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // === Step 3: poll DURING 30s ===
      pollForTimeChange({
        unitId,
        lastTime,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Fault cleared after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === Step 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},
            {
              headers: {
                id: "anything",
                "Content-Type": "application/json",
              },
            }
          );

          console.log("🟢 P command sent, polling again...");

          // === Step 5: poll AFTER P ===
          pollForTimeChange({
            unitId,
            lastTime,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Fault cleared after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const disableWell = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [wellConfig.DISABLE_WELL];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      cmd += "," + (wellConfig.DISABLE_WELL - 40001) + ",0";
      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
         {unitId, cmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd }`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            {unitId, cmd: pCmd ,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd }`,},

            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const startFaultClear = [
    { label: "Reset/Fault Clear", submit: handleSubmitFaultClear },
    { label: "Start Plunger Lift", submit: handleSubmitStartPlgLift },
    { label: "Stop Plunger Lift", submit: handleSubmitStopPlgLift },
    { label: "Disable Well", submit: handleSubmitDisableWell },
  ];

  return (
    <Grid
      container
      spacing={4}
      sx={{
        backgroundColor: "#374151",
        color: "#fff",
        p: 2,
        width: "100%",
      }}
    >
      {/* Emergency Shutdowns */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ border: "1px solid #fff" }}>
          <Box sx={{ bgcolor: "#1e293b", px: 1, py: 1 }}>
            <Typography
              sx={{ color: "#fff", fontWeight: "bold", fontSize: 14 }}
            >
              Emergency Shutdowns
            </Typography>
          </Box>

          {emergencyValues.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #827f7fff",
                py: 1.2,
                px: 1,
              }}
            >
              <Typography
                sx={{
                  flex: 2,
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {item.label}
              </Typography>

              <Grid
                container
                spacing={1}
                alignItems="center"
                sx={{ flex: 3, justifyContent: "flex-end" }}
              >
                <Grid>
                  <Select
                    fullWidth
                    size="small"
                    style={{ fontSize: 13 }}
                    value={item.value ?? ""}
                    onChange={(e) => handleChange(item.label, e.target.value)}
                    sx={{
                      backgroundColor: "#1f2937",
                      color: "#fff",
                      ".MuiSelect-icon": { color: "#fff" },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      fontWeight: 600,
                    }}
                  >
                    <MenuItem value="1" style={{ fontSize: 13 }}>
                      Enabled
                    </MenuItem>
                    <MenuItem value="0" style={{ fontSize: 13 }}>
                      Disabled
                    </MenuItem>
                  </Select>
                </Grid>

                <Grid>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{ fontSize: 12 }}
                    onClick={() => handleSubmit(item.label)}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Box>
      </Grid>

      {/* Start & Fault Clear */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Box sx={{ border: "1px solid #fff" }}>
          <Box sx={{ bgcolor: "#1e293b", px: 1, py: 1 }}>
            <Typography
              sx={{ color: "#fff", fontWeight: "bold", fontSize: 14 }}
            >
              Start & Fault Clear
            </Typography>
          </Box>

          {startFaultClear.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                borderBottom: "1px solid #827f7fff",
                py: 1.2,
                px: 1,
              }}
            >
              <Typography
                sx={{
                  flex: 2,
                  color: "#ffffffff",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {item.label}
              </Typography>

              <Box
                sx={{ flex: 3, display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ fontSize: 12 }}
                  size="small"
                  onClick={item.submit}
                >
                  Submit
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Grid>
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Grid>
  );
}
